import {
	AppSettings,
	AppUser,
	AppConfig,
	LoggingConfig,
	AssetStore,
	AppMessage,
	AppCulture,
	AppSiteDto,
	AppSiteCollectionDto,
	// #region Commerce
	SiteCategoryTreeDto,
	ReplacementTokenFields,
	CurrencyDto,
	// #endregion
} from 'types';
import { SettingGroupResults } from 'features/settings/__models__/Groups';
import RmsMessage from '~/features/message-center/__models__/RmsMessage';

// handle the initial settings here
const initialContext: AppState = (window as any).__ROC_PRELOADED_STATE__;

// This is dangerous if the state ever changes. If it is ever found out that we do change AppContextState, this export of the initial state isn't sufficient.
export default initialContext;

/**
 * Interface for the global application context
 */
export interface AppState {
	settings: AppSettings;
	config: AppConfig;
	logging: LoggingConfig;
	user: AppUser;
	assetStores: AssetStore[];
	dynamicEntityMetadata: DynamicEntityMetadata;
	messages: AppMessage[];
	configurationUpdatedOnServerSide: boolean;
	settingGroups: SettingGroupResults;
	cultures: AppCulture[];
	sites: AppSiteDto[];
	siteCollections: AppSiteCollectionDto[];
	tenantId: string;
	/**
	 * Tenant base URL.
	 * Required so that react-router knows what the "base route" should be, so that it do correct relative routing
	 */
	tenantBaseUrl: string;
	documentUrls: Record<string, string>;
	rmsMessages: RmsMessage[];
	// #region Commerce
	categoryTrees: SiteCategoryTreeDto[];
	availableCurrencies: string[];
	defaultCurrency: CurrencyDto;
	// #endregion
	initialServiceStatus: ServiceStatus;
	color: string;
}

/**
 * Information about required Roc services
 */
interface ServiceStatus {
	/**
	 * Whether RabbitMQ is connected for Roc.Admin
	 */
	rabbitMqConnected: boolean;
}

/**
 * Contains runtime defined details for all entity types in the system
 */
interface DynamicEntityMetadata {
	/**
	 * The entity definition map.
	 * Key: "/ajax/commerce/products"
	 * Value: { singularName: "Product", pluralName: "Product", IsVersionable: true, ... }
	 */
	entityDefinitionMap: Record<string, DynamicEntityDefinition>;
}

/**
 * Represents details about a Domain Entity to facilitate UI rendering
 */
interface DynamicEntityDefinition {
	/** The singular name of the entity (eg: Product) */
	singularName: string;

	/** The plural name of the entity (eg: Product) */
	pluralName: string;

	/** Entity implements IVersionableModel */
	isVersionable: boolean;

	/** Entity supports Import */
	isImportable: boolean;

	/** Entity supports Export */
	isExportable: boolean;

	/** Entity supports the download tool */
	isDownloadable: boolean;

	/** Entity implements IHierarchyModel */
	isHierarchy: boolean;

	/** Entity implements INamedModel */
	isNamedModel: boolean;

	/** Entity implements IUniqueCodeModel */
	isUniqueCodeModel: boolean;

	/** Entity implements ICodeModel */
	isCodeModel: boolean;

	/** Entity implements ISortableModel */
	isSortable: boolean;

	/** Entity implements IScopedModel */
	isScoped: boolean;

	/** Entity implements ICultureModel */
	isTranslatable: boolean;

	/** Entity implements IPermanentModel */
	isPermanentModel: boolean;

	/** Entity implements ISoftDeletable */
	isSoftDeletable: boolean;

	/** Entity implements IUserTrackedModel */
	isUserTrackedModel: boolean;

	/** Entity implements IUserModel */
	isUserModel: boolean;

	/** Entity implements INullableUserModel */
	isNullableUserModel: boolean;

	/** Entity implements IBasicModel */
	isBasicModel: boolean;

	/** Entity implements IBaseModel */
	isBaseModel: boolean;

	/** Entity implements ISeoModel */
	isSeoModel: boolean;

	/** Entity implements IExternalModel */
	isExternalModel: boolean;

	/** Entity implements ISearchExcludable */
	isExcludedFromSearchModel: boolean;

	/** Entity has fields that allow for replacement tokens */
	replacementTokenFields: ReplacementTokenFields | null;
}
